import AddIcon from '@mui/icons-material/Add';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Avatar, Box, Grid, MenuItem, Select } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { fetchMatchingJobs, updateConsultantAvailability } from 'wisestep-api';
import {
  BottomActionBar,
  CardVariant,
  WsButton,
  WsCard,
  WsColors,
  WsDataTable,
  WsIconText,
  WsListingLoader,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import useJobsStore from '../../../../../features/jobs/listing/hooks/useJobsStore';
import { ReactComponent as NoCandidates } from '../../../../../images/NoCandidates.svg';
import { emptyConsultantFilter } from '../../../helper/EmptyConsultantFilter';
import { matchingJobQuery } from '../../../helper/MatchingJobQuery';
import useConsultantsListing from '../../hooks/useConsultantsListing';
import useConsultantsStore from '../../hooks/useConsultantsStore';

import BulkUpdate from './BulkUpdate';
import ConsultantInfoDrawer from './ConsultantInfoDrawer';

const BenchConsultants = () => {
  const [openConsultantDrawer, setOpenConsultantDrawer] = useState({ open: false, default: 0 });
  const [openBulkUpdate, setOpenBulkUpdate] = useState<boolean>(false);
  const [selectedConsultant, setSelectedConsultant] = useState(0);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [consultants] = useConsultantsListing(emptyConsultantFilter, setLoader);
  const [selectedConsultantLength, setSelectedConsultantLength] = useState(0);
  const navigate = useNavigate();
  const [matchedJobsCount, setMatchedJobsCount] = useState([]);
  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Job Title',
      accessor: 'jobTitle',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
          {cell.value}
        </WsTypography>
      ),
    },
    {
      Header: 'Location',
      accessor: 'location',
    },
    {
      Header: 'Availability',
      accessor: 'availability',
      Cell: (cell: Cell) => (
        <Select
          onChange={async (e) => {
            consultants[cell.row.index].availability = e.target.value;
            await updateConsultantAvailability(consultants[cell.row.index]);
          }}
          defaultValue={cell.value}
        >
          {availabilityOptions.map((option) => (
            <MenuItem value={option.value} key={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      Header: 'Experience',
      accessor: 'experience',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
          {cell.value}
        </WsTypography>
      ),
    },
    {
      Header: 'Visa',
      accessor: 'visa',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
          {cell.value}
        </WsTypography>
      ),
    },
    {
      Header: 'Matched Jobs',
      accessor: 'matchedJobs',
      Cell: (cell: Cell) => (
        <Box display="flex" justifyContent="space-between">
          <WsIconText icon={<WorkOutlineOutlinedIcon fontSize="small" />} text={cell.value} />
        </Box>
      ),
    },
  ];

  const availabilityOptions: WsSelectObj[] = [
    {
      label: 'Not Available',
      value: 'NotAvailable',
    },
    {
      label: 'Available Immediately',
      value: 'AvailableImmediately',
    },
    {
      label: 'Available in 7 days',
      value: 'AvailableIn7Days',
    },
    {
      label: 'Available In 15 Days',
      value: 'AvailableIn15Days',
    },
    {
      label: 'Available In 30 Days',
      value: 'AvailableIn30Days',
    },
  ];

  const getConsultants = () => selectedRows.map((rowId) => consultants[rowId]);

  useEffect(() => {
    const matchedJobsData = async () => {
      const response = await fetchMatchingJobs(matchingJobQuery(consultants));
      setMatchedJobsCount(response.data);
    };
    if (consultants.length !== 0) {
      matchedJobsData();
    }
  }, [consultants]);

  const consultantTableData = consultants?.map((consultant) => ({
    name: consultant.name,
    jobTitle: consultant.jobTitle,
    location: consultant.location?.city,
    availability: consultant.availability,
    experience: consultant.totalYearsExperience,
    visa: consultant.visaStatus,
    matchedJobs: matchedJobsCount[consultant.id],
  }));

  const setConsultants = useConsultantsStore((state) => state.setSelectedConsultants);
  const setBulkJobs = useJobsStore((state) => state.updateSelectedJobs);

  useEffect(() => {
    setConsultants([]);
    setBulkJobs(null);
  }, [setConsultants, setBulkJobs]);
  return (
    <Grid container xs={12} height="30rem">
      {consultants && !loader ? (
        <>
          <Grid item xs={12}>
            <WsDataTable
              columns={columns}
              data={consultantTableData}
              boldItems={['name']}
              headless={false}
              preSelectedRows={[]}
              cellCallbacks={[
                {
                  id: 'name',
                  callback: (name, rowId) => {
                    setSelectedConsultant(rowId);
                    setOpenConsultantDrawer({ open: true, default: 0 });
                  },
                },
                {
                  id: 'matchedJobs',
                  callback: (name, rowId) => {
                    setSelectedConsultant(rowId);
                    setOpenConsultantDrawer({ open: true, default: 1 });
                  },
                },
              ]}
              getSelectedRows={(idArray) => {
                setSelectedConsultantLength(idArray.length);
                setSelectedRows(idArray);
              }}
              selectable
            />
          </Grid>

          <BulkUpdate
            selectedConsultants={getConsultants()}
            onClose={setOpenBulkUpdate}
            openBulkUpdate={openBulkUpdate}
          />

          {openConsultantDrawer.open && (
            <ConsultantInfoDrawer
              defaultTabNumber={openConsultantDrawer.default}
              matchedJobsCount={matchedJobsCount[consultants[selectedConsultant].id]}
              consultantData={consultants[selectedConsultant]}
              openDrawer={openConsultantDrawer.open}
              setOpenDrawer={setOpenConsultantDrawer}
              consultants={consultants}
            />
          )}

          {selectedConsultantLength > 0 && (
            <BottomActionBar>
              <Grid item container justifyContent="space-between" px={6}>
                <Grid item container xs={7} display="flex">
                  <Grid item pr={2} display="flex">
                    <Avatar
                      variant="square"
                      sx={{ bgcolor: WsColors.TheBlues400, width: 24, height: 24 }}
                    >
                      <WsTypography color={WsTypoColors.WHITE}>
                        {selectedConsultantLength}
                      </WsTypography>
                    </Avatar>
                    <WsTypography color={WsTypoColors.PRIMARY}>Consultant(s) Selected</WsTypography>
                  </Grid>
                </Grid>

                <WsButton onClick={() => setOpenBulkUpdate(true)}>Bulk Update</WsButton>
                <WsButton
                  onClick={() => {
                    setConsultants(getConsultants());
                    // localStorage.setItem('consultantData', JSON.stringify(getConsultants()));
                    navigate('/vendors');
                  }}
                >
                  Market Consultants to Vendors
                </WsButton>
              </Grid>
            </BottomActionBar>
          )}
        </>
      ) : consultants && loader ? (
        <Grid item xs={12}>
          <WsListingLoader />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <WsCard
            cardType={CardVariant.WITH_OUT_BG}
            title="There are no Bench Consultants"
            description="Upload Bench Consultants to your workspace"
            middleComponent={<NoCandidates />}
            isButton={true}
            buttonText="Add/Import Consultants"
            btnStartIcon={<AddIcon />}
            onClick={() => navigate('/consultant/uploads')}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default BenchConsultants;
